/* eslint-disable @typescript-eslint/await-thenable */
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable react-hooks/exhaustive-deps */
import { subDays } from "date-fns";
import { ITableProps, kaReducer } from "ka-table";
import {
  hideLoading, loadData, updateData, updatePageIndex, updatePagesCount,
} from "ka-table/actionCreators";
import { ActionType, SortingMode } from "ka-table/enums";
import { DispatchFunc } from "ka-table/types";
import {
  useEffect, useMemo, useRef, useState,
} from "react";
import { useOutletContext, useParams } from "react-router-dom";
import uuid from "react-uuid";
import useLocalStorageState from "use-local-storage-state";
import { getCompanyEventActivity, getCompanyEventList } from "../../../../services/companyEndpoint";
import KaTable, { IActionPayload } from "../../../../shared/table/KaTable";
import dateConverter from "../../../../utils/dateConverter";
import LeftEventsBar from "./components/LeftEventsBar";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";

const AccountEvents = () => {
  const workspaceId = useWorkspaceStore((state) => state.workspace?.id);
  const tableRef = useRef<any>(null);

  // Updated time range options to match Vitally's design
  const metricList = useMemo(() => [
    { id: 1, name: "Last 24 hours" },
    { id: 2, name: "Last 7 days" },
    { id: 3, name: "Last 30 days" },
    { id: 4, name: "Last 90 days", default: true },
    { id: 5, name: "Last 180 days" },
    { id: 6, name: "Last 365 days" },
    { id: 7, name: "Custom range" }
  ], []);
  
  const tableHeight = useMemo(
    // eslint-disable-next-line no-unsafe-optional-chaining
    () => window.innerHeight - 365, // Added extra space for the header
    [],
  );

  // Updated table columns to match Vitally's design
  const [tableColumns, setTableColumns] = useLocalStorageState<any>("he-events-prefs", {
    defaultValue: [
      {
        key: "users.name",
        dataType: "string",
        title: "User",
        isResizable: true,
        colGroup: { style: { minWidth: 180 } },
        isSortable: true,
      },
      {
        key: "timestamp",
        dataType: "date",
        title: "Timestamp",
        isResizable: true,
        colGroup: { style: { minWidth: 180 } },
        isSortable: true,
      },
      {
        key: "traits",
        dataType: "object",
        title: "Event Properties",
        isResizable: true,
        colGroup: { style: { minWidth: 250 } },
        isSortable: false,
      },
    ],
  });

  const [tableProps, changeTableProps] = useState<ITableProps>({
    columns: tableColumns,
    columnResizing: true,
    noData: { text: "No results found" },
    paging: {
      enabled: true,
      pageIndex: 0,
      pagesCount: 1,
      pageSize: 20,
    },
    columnReordering: true,
    rowKeyField: "randomId",
    height: tableHeight,
    // Removed singleAction: loadData() to prevent duplicate API calls
    sortingMode: SortingMode.Single,
    loading: {
      enabled: true,
      text: "Loading",
    },
  });

  const [noResults, setNoResults] = useState<boolean>(false);
  const params: any = useParams();
  const [events, setEvents] = useState<any[]>([]);
  const [metric, setMetric] = useState <any>(metricList[3]);
  const [selectedEvent, setSelectedEvent] = useState<any>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [query, setQuery] = useState<string>("");
  const [total, setTotal] = useState<number>(0);
  const firstRender = useRef<boolean>(true);
  // Add a ref to track if the effect has run for the current metric
  const effectRan = useRef<{ [key: string]: boolean }>({});

  const calcDate: any = useMemo(() => {
    if (!metric?.name) {
      return new Date("1-1-1970");
    }
    
    // Handle "Last 24 hours" specifically
    if (metric.name === "Last 24 hours") {
      // Exactly 24 hours ago
      const now = new Date();
      return new Date(now.getTime() - 24 * 60 * 60 * 1000);
    }
    
    // For other time ranges, extract the number and use subDays
    const val = parseInt(metric.name.match(/[0-9]+/) ? metric.name.match(/[0-9]+/)[0] : "1", 10);
    return subDays(new Date(), val);
  }, [metric?.name]);

  // Use a ref to store the previous metric name and query to prevent duplicate API calls
  const prevMetricRef = useRef<string>("");
  const prevQueryRef = useRef<string>("");
  
  // Fetch event list only when the time range or search query changes
  useEffect(() => {
    // Skip if no metric name
    if (!metric?.name) return;
    
    // Only fetch if metric or query has changed
    if (prevMetricRef.current === metric.name && prevQueryRef.current === query) {
      return;
    }
    
    // Update refs with current values
    prevMetricRef.current = metric.name;
    prevQueryRef.current = query;
    
    const getTable = async () => {
      setLoading(true);
      try {
        const data = await getCompanyEventList({
          workspace_identifier: workspaceId,
          search_query: query,
          external_id: params.accountId,
          from_date: dateConverter(calcDate),
        });
        setEvents(data?.data?.results);
        if (firstRender.current && data?.data?.results?.length > 0) {
          setSelectedEvent(data?.data?.results[0]?.event_name);
          firstRender.current = false;
        } else if (data?.data?.results?.length === 0) {
          // Clear selected event if no events are found
          setSelectedEvent("");
        }
      } catch (e: any) {
        setEvents([]);
        setSelectedEvent("");
      }
      setLoading(false);
    };
    
    getTable();
  }, [query, metric?.name, calcDate, workspaceId, params.accountId]);

  const handleQuery = (e: any) => {
    setQuery(e.target.value);
  };

  const onColumnChange = (payload: IActionPayload) => {
    const {
      type, columnKey, width, targetColumnKey,
    } = payload;

    if (type === ActionType.ResizeColumn) {
      const newFields = tableColumns.map((field: any) => {
        if (field.key === columnKey) {
          field.width = width;
        }
        return field;
      });

      setTableColumns(newFields);
    }

    if (type === ActionType.ReorderColumns) {
      const columnIndex = tableColumns.findIndex((field: any) => field.key === columnKey);
      const targetColumnIndex = tableColumns.findIndex(
        (field: any) => field.key === targetColumnKey,
      );
      const newFields = [...tableColumns];
      const temp = newFields[columnIndex];
      newFields[columnIndex] = newFields[targetColumnIndex];
      newFields[targetColumnIndex] = temp;
      // shift the column to the target column index.
      setTableColumns(newFields);
    }
  };

  const kaDispatch: DispatchFunc = (action: any) => {
    changeTableProps((prevState: ITableProps) => kaReducer(prevState, action));
    const sortColumn: any = tableProps.columns.find((c) => c.sortDirection);
    if (action.type === ActionType.ResizeColumn || action.type === ActionType.ReorderColumns) {
      if (action.type === ActionType.ResizeColumn) {
        onColumnChange({
          type: action.type,
          columnKey: action.columnKey,
          width: action.width,
        });
      } else {
        onColumnChange({
          type: action.type,
          columnKey: action.columnKey,
          targetColumnKey: action.targetColumnKey,
        });
      }
    }
  };

  const fetchData = async (page: number) => {
    // Only fetch data if an event is selected
    if (!selectedEvent) {
      setTotal(0);
      kaDispatch(updateData([]));
      kaDispatch(updatePagesCount(0));
      kaDispatch(hideLoading());
      return;
    }
    
    try {
      const response = await getCompanyEventActivity({
        workspace_identifier: workspaceId,
        event_name: selectedEvent,
        external_id: params.accountId,
        from_date: dateConverter(calcDate),
        offset: page * 20,
        limit: 20,
      });
      if (response.data) {
        const temp = response?.data?.results.map((item: any) => {
          const value = { ...item };
          if (value?.name || value?.email) {
            value["users.name"] = {
              name: value?.name,
              desc: value?.email,
              img: value?.avatar_url,
              externalId: value?.user_id,
              relation: "user",
              accountId: params.accountId,
            };
            delete (value.name);
            delete (value.email);
            delete (value?.avatar_url);
            delete (value?.user_id);
          }
          value.randomId = uuid();
          return value;
        });
        setTotal(response.data.total_hits);
        kaDispatch(updateData(temp));
        if (response.data.total_hits < ((tableProps?.paging?.pageIndex || 0) * (tableProps?.paging?.pageSize || 1))) {
          kaDispatch(updatePageIndex(0));
        }
        kaDispatch(updatePagesCount(Math.ceil(response.data.total_hits / (tableProps?.paging?.pageSize ?? 1)) || 0));
        kaDispatch(hideLoading());
      } else {
        setTotal(0);
        setNoResults(true);
        kaDispatch(updatePageIndex(0));
        kaDispatch(updateData([]));
        kaDispatch(updatePagesCount(1));
        kaDispatch(hideLoading());
      }
    } catch (err) {
      setTotal(0);
      setNoResults(true);
      kaDispatch(updatePageIndex(0));
      kaDispatch(updateData([]));
      kaDispatch(updatePagesCount(1));
      kaDispatch(hideLoading());
    }
  };

  useEffect(() => {
    fetchData(tableProps?.paging?.pageIndex || 0);
  }, [tableProps?.paging?.pageIndex, selectedEvent]);

  return (
    <div className="flex flex-col h-full border rounded-lg overflow-hidden">
      {/* Left sidebar with events list */}
      <LeftEventsBar
        selected={selectedEvent}
        setSelected={setSelectedEvent}
        events={events}
        query={query}
        onChange={(e: any) => { setQuery(e.target.value); }}
        loading={loading}
      />
      {/* Main content area */}
      <div className="flex-1 bg-white">
        {/* Header with event name, count, and time filter */}
        <div className="flex justify-between items-center py-3 px-4 border-b border-gray-200">
          <div className="flex items-center">
            <h2 className="text-xl font-semibold text-gray-800">
              {selectedEvent ? selectedEvent.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) : "Select an event"}
              {total > 0 && (
                <span className="ml-3 text-sm bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                  {total} events
                </span>
              )}
            </h2>
          </div>
          {/* Time filter dropdown - moved to header as in Vitally */}
          <div className="flex items-center">
            <div className="relative">
              <button
                type="button"
                className="inline-flex items-center gap-x-1 text-sm font-medium text-gray-700 bg-white px-3 py-2 rounded-md border border-gray-300 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                onClick={() => {
                  // Toggle dropdown visibility
                  const dropdown = document.getElementById('time-dropdown');
                  if (dropdown) {
                    dropdown.classList.toggle('hidden');
                  }
                }}
              >
                {metric?.name || 'Select time range'}
                <svg className="-mr-1 h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                </svg>
              </button>
              {/* Dropdown menu */}
              <div
                id="time-dropdown"
                className="hidden absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
              >
                {metricList.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`block w-full text-left px-4 py-2 text-sm ${metric?.id === item.id ? 'bg-purple-100 text-purple-700' : 'text-gray-700 hover:bg-gray-50'}`}
                    onClick={() => {
                      setMetric(item);
                      // Hide dropdown after selection
                      const dropdown = document.getElementById('time-dropdown');
                      if (dropdown) {
                        dropdown.classList.add('hidden');
                      }
                      // Clear selected event if changing time range
                      setSelectedEvent('');
                    }}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Table content */}
        <div>
          {selectedEvent ? (
            <KaTable
              tableProps={tableProps}
              changeTableProps={changeTableProps}
              kaDispatch={kaDispatch}
              ref={tableRef}
              totalData={total}
              itemType="Events"
              rowKeyField="randomId"
              height={tableHeight}
            />
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-medium text-gray-700 mb-2">Select an event to view details</h3>
              <p className="text-gray-500 max-w-md">Choose an event from the list on the left to see its activity feed and properties</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountEvents;
